import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Infinity as Ganzjaehrig, Plus } from 'lucide-react';
import { CyclingEvent, EVENT_STATUS_LABEL, EVENT_STATUS_TEXT, eventCardModel, plannerEventModel, splitMonYear } from '@radevents/shared';
import { FreundeAmEvent } from '../EventZeile';
import BookmarkIcon from '../BookmarkIcon';

/**
 * Ein Event in Meine Events, als Zeile (30.10.2026).
 *
 * Vorher eine Karte mit eigener Fußzeile nur für den Statuswähler: am Rechner 880px breit, die
 * Datumsspanne brach in zwei Zeilen („23–“ über „25“), und je Event standen rund 180px Höhe für
 * drei Zeilen Text. Jetzt dieselbe Datumskachel wie auf der Startseite (`ez-kachel`, Monatsband in
 * der Disziplinfarbe), Status und Lesezeichen rechts in der Zeile, auf dem Handy der Status unter
 * dem Ort. Vorbild Luma („Your Events“, Status am Event) und Posh (Agenda nach Datum).
 */
export default function PlannerEventCard({ event, onBookmark, onParticipation }: {
  event: CyclingEvent; onBookmark: () => Promise<void> | void; onParticipation: (going: boolean) => Promise<void> | void;
}) {
  const model = plannerEventModel(event);
  const card = eventCardModel(event);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async (action: () => Promise<void> | void) => {
    setBusy(true); setError('');
    try { await action(); } catch { setError('Änderung konnte nicht gespeichert werden.'); }
    finally { setBusy(false); }
  };
  const color = model.cancelled ? '#B9B3B3' : card.categoryColor;
  const friends = event.friendInterests ?? [];

  return <article className={`planner-card${model.cancelled ? ' planner-card--abgesagt' : ''}`}>
    <div className="ez-kachel planner-kachel" style={{ ['--ez-farbe' as string]: color }}
      aria-label={model.date ? `${model.date.day} ${model.date.mon}` : model.permanentLabel ?? 'Jederzeit'}>
      {model.date ? <>
        <span className="ez-monat">{splitMonYear(model.date.mon).month}</span>
        <span className={model.date.day.length > 2 ? 'ez-tag ez-tag--spanne' : 'ez-tag'}>{model.date.day}</span>
        <span className="ez-wochentag">{model.date.weekday ? model.date.weekday.slice(0, 2) : ''}</span>
      </> : <>
        <span className="ez-monat">Immer</span>
        <span className="ez-tag"><Ganzjaehrig size={20} aria-hidden /></span>
        <span className="ez-wochentag" />
      </>}
    </div>

    <div className="planner-card-info">
      {/* Oben Zustand (wie in der Event-Zeile der Startseite), Disziplin und Eventart; die
          Kilometer stehen in der Ortszeile, wo sie nicht abgeschnitten werden (in Versalien passte
          „RENNRAD · ULTRACYCLING · 180–520 KM“ nicht). */}
      <p className="planner-card-details" style={{ color }} title={model.categoryLabel}>
        {model.status && <><span className="ez-status" style={{ color: EVENT_STATUS_TEXT[model.status] }}>{EVENT_STATUS_LABEL[model.status]}</span> · </>}
        {model.categoryLabel}
      </p>
      <h3 className={model.cancelled ? "planner-card-cancelled" : undefined}><Link className="planner-card-link" to={`/events/${event.id}`}>{event.title}</Link></h3>
      <p className="planner-card-location">
        <span className="planner-card-ort">{model.location}</span>
        {model.distanceLabel && <span className="planner-card-km">{model.distanceLabel}</span>}
      </p>
      {model.friendLabel && <p className="planner-card-friends"><FreundeAmEvent friends={friends} className="planner-card-freunde" /></p>}
    </div>

    {/* Ein Schalter statt einer Auswahl „Vorgemerkt ▾“ (30.10.2026): dass das Event vorgemerkt ist,
        sagt schon das Lesezeichen. Bleibt nur die Frage, ob man dabei ist. */}
    <button type="button" className={`planner-dabei${event.participating ? ' planner-dabei--an' : ''}`} aria-pressed={!!event.participating}
      title={event.participating ? 'Zusage zurücknehmen' : 'Deinen Freunden zeigen, dass du dabei bist'}
      disabled={busy} onClick={() => void run(() => onParticipation(!event.participating))}>
      {event.participating ? <Check size={15} aria-hidden /> : <Plus size={15} aria-hidden />}Ich bin dabei
    </button>
    <button type="button" className="planner-bookmark" aria-label={event.favorited ? 'Vormerkung entfernen' : 'Event vormerken'} aria-pressed={!!event.favorited} disabled={busy} onClick={() => void run(onBookmark)}><BookmarkIcon filled={!!event.favorited} size={20} /></button>
    {error && <p className="planner-card-error" role="alert">{error}</p>}
  </article>;
}
